'use client';

import { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Check, FileText, ImageOff, ArrowRight, Plus, Loader2 } from 'lucide-react';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
  SettingsCard,
  PickerRow,
} from '@/shared/ui';
import { LoadingSpinner } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { useSelectableProjectReferences } from '@/features/project-reference/hooks/use-project-references';
import {
  ProjectReferenceUpload,
  type ProjectReferenceUploadRef,
} from '@/features/project-reference/ui/ProjectReferenceUpload';
import type {
  ProjectReference,
  ProjectReferenceAspect,
  ProjectReferenceCategory,
} from '@/types/project-reference';
import { isImageReference, hasAspect } from '@/types/project-reference';

interface ProjectReferenceSheetProps {
  projectId: string;
  category: ProjectReferenceCategory;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  maxSelection?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Callback to navigate to the project references management page */
  onNavigateToReferences?: () => void;
  /**
   * Automatic mode (use all aspect refs). Aspect variant only — the Output
   * selector leaves these undefined and shows no Automatic/Upload rows.
   */
  useAllRefs?: boolean;
  onSelectAll?: () => void;
  /**
   * Picker behavior (gap #5 — this sheet is shared with the Output-style selector):
   *  - `aspect` (default): the style/material/color axes. ONE flat list of all
   *    inspiration images, each showing its aspect chips; selection is free per
   *    axis regardless of tags (tags are a recommendation, not a restriction).
   *  - `output`: the Output-style selector. Keeps the legacy category split
   *    (matching `output` first, others below with a category-change badge) and
   *    its single-select `max:1` behavior — untouched by the aspects work.
   */
  variant?: 'aspect' | 'output';
}

function getReferenceImageUrl(reference: ProjectReference): string | null {
  if (reference.attributes?.file_url) {
    return reference.attributes.file_url;
  }
  return null;
}

/**
 * Controlled selection sheet for project references. Holds the grid of
 * matching/other references and the category-change affordance; the trigger
 * surface (a settings row, a reference-source radio, a compact button) lives
 * in the caller and drives `open`. Shared by `ProjectReferenceSelector`
 * (output style) and `ReferenceSourceGroup` (style/materials/colors).
 */
export function ProjectReferenceSheet({
  projectId,
  category,
  selectedIds,
  onSelectionChange,
  maxSelection = 3,
  open,
  onOpenChange,
  onNavigateToReferences,
  useAllRefs = false,
  onSelectAll,
  variant = 'aspect',
}: ProjectReferenceSheetProps) {
  const t = useTranslations('projectReference');
  const tRender = useTranslations('render.settings.projectReference');
  const uploadRef = useRef<ProjectReferenceUploadRef>(null);
  // Optimistic local preview: the crop modal emits the picked image synchronously
  // on confirm (before the background upload starts), so the freshly uploaded
  // card shows instantly — matching Apple's in-grid optimistic card. Cleared once
  // the real (pending) reference lands in the list. See ProjectReferenceUpload.
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);

  // Inline upload: the new reference is seeded with THIS axis's aspect, and the
  // classifier merges its own aspects on top (superset). After confirm we
  // auto-select it — but only when NOT in Automatic mode. In Automatic mode the
  // ref is already picked up by the aspect-matched auto pool; forcing its id
  // into the manual list would silently flip Automatic → manual.
  // Output picks ONE reference (single-select) which becomes the style; the
  // aspect axes are multi-select with an Automatic (use-all) option.
  const isOutput = variant === 'output';
  const isAutoMode = useAllRefs && selectedIds.length === 0;
  const handleUploadSuccess = (reference: ProjectReference) => {
    // Output is single-select → the new ref replaces the selection.
    if (isOutput) {
      onSelectionChange([reference.id]);
      return;
    }
    if (isAutoMode) return;
    if (selectedIds.includes(reference.id)) return;
    onSelectionChange([...selectedIds, reference.id]);
  };

  // Fetch ALL project references (no category filter). Includes in-flight
  // (pending/processing) uploads so a freshly uploaded card stays visible with
  // a processing overlay instead of vanishing until classification completes.
  const { data: allReferences = [], isLoading } =
    useSelectableProjectReferences(projectId, {
      enabled: !!projectId,
    });

  // Manual grid pool. The Output picker hard-filters to render-style refs only
  // (category `output`); the aspect axes drop output refs and surface the ones
  // carrying THIS axis's aspect first — most-suitable-first (a recommendation,
  // not a restriction; selection stays free regardless of order).
  const images = allReferences.filter(isImageReference);
  const aspect = category as ProjectReferenceAspect;
  const axisRefs = images.filter((r) => r.attributes.category !== 'output');
  // Stable partition keeps the recency order within each group.
  const manualRefs = isOutput
    ? images.filter((r) => r.attributes.category === 'output')
    : [
        ...axisRefs.filter((r) => hasAspect(r, aspect)),
        ...axisRefs.filter((r) => !hasAspect(r, aspect)),
      ];

  const handleToggle = (referenceId: string) => {
    if (selectedIds.includes(referenceId)) {
      onSelectionChange(selectedIds.filter((id) => id !== referenceId));
      return;
    }

    // Single-select pickers (e.g. output style) replace the current selection
    // on tap; without this branch tapping a second reference is a no-op while
    // one is selected.
    if (maxSelection === 1) {
      onSelectionChange([referenceId]);
      return;
    }

    if (selectedIds.length < maxSelection) {
      onSelectionChange([...selectedIds, referenceId]);
    }
  };

  const handleManageClick = () => {
    onOpenChange(false);
    onNavigateToReferences?.();
  };

  return (
    <NestedSheet open={open} onOpenChange={onOpenChange}>
      <NestedSheetContent>
        {/* Apple parity: short category-only title ("Style reference",
            "Colors", "Material sample", "Output style"). The "Select up to
            N references" subtitle inside the body conveys the action. */}
        <NestedSheetHeader>
          <NestedSheetTitle>{t(`category.${category}`)}</NestedSheetTitle>
        </NestedSheetHeader>

        <NestedSheetBody className="px-4 space-y-4">
          {/* Automatic selection (aspect axes only — Output is single-select). */}
          {!isOutput && onSelectAll && (
            <SettingsCard>
              <PickerRow
                title={tRender('automaticSelection')}
                isSelected={isAutoMode}
                onClick={onSelectAll}
              />
            </SettingsCard>
          )}

          {/* Manual selection — grouped card. Upload "+" sits in the header when
              references exist, centered inside the card when empty. The grid is
              container-responsive (2 narrow → 3 on a wider panel), no tags. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <span className="text-section-eyebrow text-muted-foreground">
                {tRender('manualSelection')}
              </span>
              {!isLoading && (manualRefs.length > 0 || pendingPreview) && (
                <button
                  type="button"
                  aria-label={tRender('upload')}
                  onClick={() => uploadRef.current?.openFileDialog()}
                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:opacity-90"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="rounded-2xl bg-settings-card p-3">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : manualRefs.length === 0 && !pendingPreview ? (
                <button
                  type="button"
                  onClick={() => uploadRef.current?.openFileDialog()}
                  className="flex w-full flex-col items-center gap-2 py-6"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Plus className="h-5 w-5" />
                  </span>
                  <span className="text-list-item-subtitle text-muted-foreground">
                    {tRender('upload')}
                  </span>
                </button>
              ) : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(8rem,1fr))] gap-3">
                  {/* Optimistic local-preview tile — shown instantly on crop
                      confirm, replaced by the real pending card once it lands. */}
                  {pendingPreview && (
                    <div className="flex flex-col items-center gap-1.5">
                      <div className="relative aspect-[4/3] w-full rounded-lg overflow-hidden border-2 border-border">
                        <img
                          src={pendingPreview}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
                          <Loader2 className="w-6 h-6 animate-spin text-white" />
                        </div>
                      </div>
                    </div>
                  )}
                  {manualRefs.map((reference) => {
                    const isRefSelected = selectedIds.includes(reference.id);
                    const isDisabled =
                      maxSelection > 1 &&
                      !isRefSelected &&
                      selectedIds.length >= maxSelection;
                    const imageUrl = getReferenceImageUrl(reference);

                    return (
                      <ReferenceGridItem
                        key={reference.id}
                        reference={reference}
                        isImage={isImageReference(reference)}
                        imageUrl={imageUrl}
                        isSelected={isRefSelected}
                        isDisabled={isDisabled}
                        onToggle={() => handleToggle(reference.id)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Manage all references link */}
          {onNavigateToReferences && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={handleManageClick}
                className="inline-flex items-center gap-1 text-list-item-subtitle text-primary hover:underline"
              >
                {tRender('manageAll')}
                <ArrowRight className="w-4 h-4 rtl:-scale-x-100" />
              </button>
            </div>
          )}

          {/* Headless uploader. Aspect axes seed the matching aspect; the Output
              picker tags the upload with the output role (render-time, not an
              aspect). */}
          <ProjectReferenceUpload
            ref={uploadRef}
            projectId={projectId}
            section="reference"
            {...(isOutput
              ? { category: 'output' as ProjectReferenceCategory }
              : { aspects: [category as ProjectReferenceAspect] })}
            acceptPdf={false}
            onSuccess={handleUploadSuccess}
            onUploadPreview={setPendingPreview}
            className="hidden"
          />
        </NestedSheetBody>
      </NestedSheetContent>
    </NestedSheet>
  );
}

// Sub-component for selectable references
interface ReferenceGridItemProps {
  reference: ProjectReference;
  isImage: boolean;
  imageUrl: string | null;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
}

function ReferenceGridItem({
  reference,
  isImage,
  imageUrl,
  isSelected,
  isDisabled,
  onToggle,
}: ReferenceGridItemProps) {
  const [imageError, setImageError] = useState(false);

  const showImage = isImage && imageUrl && !imageError;
  const showPlaceholder = !isImage || !imageUrl || imageError;
  // In-flight upload: render a processing overlay and block selection until the
  // server finishes classifying (an unclassified ref can't be used in a render).
  const status = reference.attributes.classification_status;
  const isPending = status === 'pending' || status === 'processing';

  return (
    <div className="flex flex-col items-center gap-1.5">
      <button
        type="button"
        onClick={onToggle}
        disabled={isDisabled || isPending}
        className={cn(
          'relative aspect-[4/3] w-full rounded-lg overflow-hidden border-2 transition-all',
          isSelected
            ? 'border-primary ring-2 ring-primary/20'
            : isDisabled
              ? 'border-border opacity-50 cursor-not-allowed'
              : 'border-border hover:border-muted-foreground'
        )}
      >
        {showImage && (
          <Image
            src={imageUrl}
            alt={reference.attributes.metadata?.original_filename || 'Reference'}
            fill
            className="object-cover"
            onError={() => setImageError(true)}
          />
        )}

        {showPlaceholder && (
          <div className="absolute inset-0 bg-muted flex items-center justify-center">
            {isImage ? (
              <ImageOff className="w-8 h-8 text-muted-foreground" />
            ) : (
              <FileText className="w-8 h-8 text-muted-foreground" />
            )}
          </div>
        )}

        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
            <Check className="w-3 h-3 text-primary-foreground" />
          </div>
        )}

        {/* Processing overlay for in-flight uploads (pending/processing) */}
        {isPending && (
          <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-white" />
          </div>
        )}

        {/* Filename overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
          <p className="text-card-meta text-white truncate">
            {reference.attributes.original_filename ||
              reference.attributes.metadata?.original_filename}
          </p>
        </div>
      </button>
    </div>
  );
}
